import type { ReactNode, SVGProps } from 'react';

const PATHS = {
  plus: <path d="M12 5v14M5 12h14" />,
  edit: (
    <>
      <path d="M4 20h4L19 9l-4-4L4 16v4z" />
      <path d="M13.5 6.5l4 4" />
    </>
  ),
  trash: (
    <>
      <path d="M4 7h16M10 11v6M14 11v6" />
      <path d="M6 7l1 12a1 1 0 001 1h8a1 1 0 001-1l1-12M9 7V4h6v3" />
    </>
  ),
  check: <path d="M5 12.5l4.5 4.5L19 7.5" />,
  close: <path d="M6 6l12 12M18 6L6 18" />,
  upload: (
    <>
      <path d="M12 16V5M7.5 9.5L12 5l4.5 4.5" />
      <path d="M5 19h14" />
    </>
  ),
  download: (
    <>
      <path d="M12 5v11M7.5 11.5L12 16l4.5-4.5" />
      <path d="M5 19h14" />
    </>
  ),
  pin: (
    <>
      <path d="M12 21s-6.5-5.7-6.5-11a6.5 6.5 0 0113 0c0 5.3-6.5 11-6.5 11z" />
      <circle cx="12" cy="10" r="2.3" />
    </>
  ),
  wifi: (
    <>
      <path d="M3.5 9.5a12 12 0 0117 0M6.5 12.8a7.7 7.7 0 0111 0M9.4 16a3.5 3.5 0 015.2 0" />
      <circle cx="12" cy="19" r="1" fill="currentColor" />
    </>
  ),
  layers: (
    <>
      <path d="M12 4l9 5-9 5-9-5 9-5z" />
      <path d="M3 14l9 5 9-5" />
    </>
  ),
  target: (
    <>
      <circle cx="12" cy="12" r="7" />
      <circle cx="12" cy="12" r="2" />
      <path d="M12 2v3M12 19v3M2 12h3M19 12h3" />
    </>
  ),
  radar: (
    <>
      <circle cx="12" cy="12" r="9" />
      <circle cx="12" cy="12" r="4.5" />
      <path d="M12 12L18 6" />
    </>
  ),
  ruler: (
    <>
      <path d="M3 16L16 3l5 5L8 21l-5-5z" />
      <path d="M7 12l2 2M10 9l2 2M13 6l2 2" />
    </>
  ),
  camera: (
    <>
      <path d="M4 8h3l1.5-2h7L17 8h3v11H4V8z" />
      <circle cx="12" cy="13" r="3.2" />
    </>
  ),
  lock: (
    <>
      <rect x="5" y="11" width="14" height="9" rx="1.5" />
      <path d="M8 11V8a4 4 0 018 0v3" />
    </>
  ),
  unlock: (
    <>
      <rect x="5" y="11" width="14" height="9" rx="1.5" />
      <path d="M8 11V8a4 4 0 017.5-2" />
    </>
  ),
  play: <path d="M8 5l11 7-11 7V5z" />,
  pause: <path d="M8 5v14M16 5v14" />,
  stop: <rect x="6" y="6" width="12" height="12" rx="1.5" />,
  broom: (
    <>
      <path d="M14 3l7 7M13 8l-9 9 3 3 9-9" />
      <path d="M4 17l3 3" />
    </>
  ),
  list: <path d="M8 6h12M8 12h12M8 18h12M4 6h.01M4 12h.01M4 18h.01" />,
  zoomIn: (
    <>
      <circle cx="11" cy="11" r="6.5" />
      <path d="M16 16l5 5M11 8v6M8 11h6" />
    </>
  ),
  zoomOut: (
    <>
      <circle cx="11" cy="11" r="6.5" />
      <path d="M16 16l5 5M8 11h6" />
    </>
  ),
  fit: <path d="M4 9V4h5M15 4h5v5M20 15v5h-5M9 20H4v-5" />,
  circle: <circle cx="12" cy="12" r="6" />,
  survey: (
    <>
      <path d="M9 4h6l1 2h3v14H5V6h3l1-2z" />
      <path d="M9 13l2 2 4-4" />
    </>
  ),
  evaluate: (
    <>
      <path d="M4 20V10M10 20V4M16 20v-7M22 20H2" />
    </>
  ),
} satisfies Record<string, ReactNode>;

export type IconName = keyof typeof PATHS;

export interface IconProps extends Omit<SVGProps<SVGSVGElement>, 'name'> {
  name: IconName;
  size?: number;
}

export function Icon({ name, size = 16, ...rest }: IconProps) {
  return (
    <svg
      className="icon"
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.9}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
      {...rest}
    >
      {PATHS[name]}
    </svg>
  );
}

export interface IconButtonProps {
  icon: IconName;
  label: string;
  onClick?: () => void;
  active?: boolean;
  danger?: boolean;
  primary?: boolean;
  disabled?: boolean;
  type?: 'button' | 'submit';
  size?: number;
  className?: string;
}

/** Icon-only button; the label becomes the tooltip and the accessible name. */
export function IconButton({
  icon,
  label,
  onClick,
  active = false,
  danger = false,
  primary = false,
  disabled = false,
  type = 'button',
  size = 16,
  className = '',
}: IconButtonProps) {
  const classes = ['btn', 'btn--icon-only'];
  if (active) classes.push('is-on');
  if (danger) classes.push('btn--danger');
  if (primary) classes.push('btn--primary');
  if (className) classes.push(className);
  return (
    <button type={type} className={classes.join(' ')} title={label} aria-label={label} aria-pressed={active || undefined} disabled={disabled} onClick={onClick}>
      <Icon name={icon} size={size} />
    </button>
  );
}
